import { useCallback } from 'react';
import useReportGenerate, { type ReportCallback } from './useReportGenerate';
import useFileLoading, { FileType } from './useFileLoading';
import { notification } from 'antd';

interface Props {
  fileName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  generate: (format: FileType) => Promise<any>;
}

export function useReportActions({ fileName, generate }: Props) {
  const report = useReportGenerate();
  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const handle = useCallback(
    async (format: FileType, onResult?: ReportCallback) => {
      try {
        setFileLoading(format);

        const result = await generate(format);

        if (!result) {
          return notification.error({
            message: 'Erro ao gerar o arquivo',
            description: 'Não foi possível gerar o arquivo. Tente novamente.',
          });
        }

        const blob = result.blob ?? result;

        if (onResult) {
          onResult(blob);
        } else {
          if (format === 'REPORT_VIEW') {
            report.openFile({
              result: blob,
              fileName,
              fileType: 'application/pdf',
            });
          } else {
            const ext = format === 'CSV' ? 'csv' : 'pdf';
            report.downloadFile({
              result: blob,
              fileName: `${fileName}.${ext}`,
            });
          }
        }
      } catch {
        notification.error({
          message: 'Erro ao gerar arquivo',
          description:
            'Ocorreu um problema ao gerar o arquivo. Verifique sua conexão ou tente novamente em instantes.',
        });
      } finally {
        setFileUnloading(format);
      }
    },
    [fileName, generate, report, setFileLoading, setFileUnloading],
  );

  return {
    handleView: () => handle('REPORT_VIEW'),
    handlePdf: () => handle('PDF'),
    handleCsv: () => handle('CSV'),
    fileLoading,
  };
}
